/**
 * Pick a binder for a selection of cards.
 *
 * Step one lists binders with how many of the selected cards already sit in
 * each. Step two only appears when some selected cards live in a different
 * binder: it names those binders and asks whether to move them too or leave
 * them where they are.
 */

import { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Folder, Check, X } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { isTradeBinder } from '../../services/tradeBinder';

export default function BinderPickerSheet({ isOpen, onClose, binders, selectedCards, onConfirm, title, note }) {
  const { theme } = useTheme();
  const [target, setTarget] = useState(null);

  const countsByBinder = useMemo(() => {
    const m = {};
    for (const c of selectedCards) if (c.binder_id) m[c.binder_id] = (m[c.binder_id] || 0) + 1;
    return m;
  }, [selectedCards]);

  const close = () => { setTarget(null); onClose(); };

  const pick = (binder) => {
    const elsewhere = selectedCards.filter((c) => c.binder_id && c.binder_id !== binder.id);
    if (elsewhere.length === 0) {
      onConfirm(binder, selectedCards.map((c) => c.id));
      close();
      return;
    }
    setTarget(binder);
  };

  const conflictGroups = useMemo(() => {
    if (!target) return [];
    const groups = {};
    for (const c of selectedCards) {
      if (!c.binder_id || c.binder_id === target.id) continue;
      groups[c.binder_id] = (groups[c.binder_id] || 0) + 1;
    }
    return Object.entries(groups).map(([id, count]) => ({
      id, count, name: binders.find((b) => b.id === id)?.name || 'another binder',
    }));
  }, [target, selectedCards, binders]);

  const moveAll = () => { onConfirm(target, selectedCards.map((c) => c.id)); close(); };
  const skipConflicts = () => {
    const ids = selectedCards.filter((c) => !c.binder_id || c.binder_id === target.id).map((c) => c.id);
    onConfirm(target, ids);
    close();
  };

  const alreadyThere = target ? selectedCards.filter((c) => c.binder_id === target.id).length : 0;
  const freeCount = selectedCards.filter((c) => !c.binder_id).length;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ ...styles.backdrop, backgroundColor: theme.overlay }}
            onClick={close}
          />
          <motion.div
            initial={{ opacity: 0, y: 100 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 100 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            style={{ ...styles.sheet, backgroundColor: theme.bgSheet }}
          >
            <div style={styles.handle} />
            <div style={styles.header}>
              <h2 style={{ ...styles.title, color: theme.text }}>
                {target ? 'Some are already in a binder' : (title || `Add ${selectedCards.length} to a binder`)}
              </h2>
              <button style={{ ...styles.closeBtn, backgroundColor: theme.bgCard, borderColor: theme.border, color: theme.textSecondary }} onClick={close} aria-label="Close">
                <X size={16} />
              </button>
            </div>

            {/* Why the sheet opened, when it opened for a reason the person
                did not ask for directly. Marking cards tradable lands here. */}
            {!target && note && (
              <div style={{ ...styles.note, color: theme.textMuted }}>{note}</div>
            )}

            {!target && (
              <div style={styles.list}>
                {binders.length === 0 && (
                  <div style={{ ...styles.empty, color: theme.textMuted }}>No binders yet. Make one from the Binder tab first.</div>
                )}
                {binders.map((b) => {
                  const here = countsByBinder[b.id] || 0;
                  return (
                    <button
                      key={b.id}
                      style={{ ...styles.row, backgroundColor: theme.bgCard, borderColor: theme.border }}
                      onClick={() => pick(b)}
                    >
                      <Folder size={18} color={isTradeBinder(b) ? '#4ade80' : theme.accent} />
                      <span style={{ ...styles.rowName, color: theme.text }}>{b.name}</span>
                      {/* A shelf, not a price list: personal cards may sit in
                          the trade binder too. Saying which one it is stops
                          that being a surprise at a show. */}
                      {isTradeBinder(b) && <span style={styles.tradeTag}>For trade</span>}
                      {here > 0 && (
                        <span style={{ ...styles.rowMeta, color: theme.textMuted }}>
                          {here} already here
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {target && (
              <div style={styles.conflict}>
                <div style={{ ...styles.conflictText, color: theme.textSecondary }}>
                  {conflictGroups.map((g) => (
                    <div key={g.id}>
                      <strong style={{ color: theme.text }}>{g.count}</strong> {g.count === 1 ? 'card is' : 'cards are'} in <strong style={{ color: theme.text }}>{g.name}</strong>
                    </div>
                  ))}
                  {(freeCount > 0 || alreadyThere > 0) && (
                    <div style={{ marginTop: 6 }}>
                      {freeCount > 0 && `${freeCount} not in any binder`}
                      {freeCount > 0 && alreadyThere > 0 && ', '}
                      {alreadyThere > 0 && `${alreadyThere} already in ${target.name}`}
                    </div>
                  )}
                </div>
                <button style={{ ...styles.primary, backgroundColor: theme.accent }} onClick={moveAll}>
                  <Check size={16} /> Move them all to {target.name}
                </button>
                <button style={{ ...styles.secondary, backgroundColor: theme.bgCard, borderColor: theme.border, color: theme.text }} onClick={skipConflicts}>
                  Skip those, add the rest
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

const styles = {
  note: { fontSize: 12, lineHeight: 1.45, padding: '0 16px 10px' },
  tradeTag: {
    flex: 'none', padding: '1.5px 7px', borderRadius: 999,
    border: '0.5px solid rgba(74,222,128,.45)', background: 'rgba(74,222,128,.14)',
    color: '#4ade80', fontSize: 9.5, fontWeight: 700, letterSpacing: '.04em',
    textTransform: 'uppercase',
  },
  backdrop: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10002 },
  sheet: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    borderRadius: '20px 20px 0 0',
    padding: '12px 20px calc(24px + var(--safe-area-bottom, 0px))',
    zIndex: 10003,
    maxHeight: '70vh',
    overflowY: 'auto',
    boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.5)',
  },
  handle: { width: '36px', height: '4px', backgroundColor: '#3a3a3a', borderRadius: '2px', margin: '0 auto 16px' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '14px' },
  title: { fontSize: '17px', fontWeight: '600', margin: 0 },
  closeBtn: {
    width: '30px', height: '30px', borderRadius: '8px', border: '1px solid', display: 'flex',
    alignItems: 'center', justifyContent: 'center', cursor: 'pointer', outline: 'none', flexShrink: 0,
    WebkitTapHighlightColor: 'transparent',
  },
  list: { display: 'flex', flexDirection: 'column', gap: '6px' },
  empty: { fontSize: '13px', padding: '12px 4px', lineHeight: 1.4 },
  row: {
    display: 'flex', alignItems: 'center', gap: '12px', padding: '13px 14px', borderRadius: '10px',
    border: '1px solid', cursor: 'pointer', textAlign: 'left', outline: 'none', WebkitTapHighlightColor: 'transparent',
  },
  rowName: { flex: 1, fontSize: '15px', fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  rowMeta: { fontSize: '12px', flexShrink: 0 },
  conflict: { display: 'flex', flexDirection: 'column', gap: '10px' },
  conflictText: { fontSize: '14px', lineHeight: 1.5, marginBottom: '6px' },
  primary: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '13px 16px',
    borderRadius: '10px', border: 'none', color: '#fff', fontSize: '14px', fontWeight: '600', cursor: 'pointer',
    outline: 'none', WebkitTapHighlightColor: 'transparent',
  },
  secondary: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '13px 16px', borderRadius: '10px',
    border: '1px solid', fontSize: '14px', fontWeight: '600', cursor: 'pointer', outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
};
